import {
    CHECK_LIST,
    ELEMENT_TRANSFORMERS,
    ElementTransformer,
    TEXT_FORMAT_TRANSFORMERS,
    TEXT_MATCH_TRANSFORMERS,
    TextMatchTransformer,
    Transformer,
} from '@lexical/markdown'
import {
    $createHorizontalRuleNode,
    $isHorizontalRuleNode,
    HorizontalRuleNode,
} from '@lexical/react/LexicalHorizontalRuleNode'
import {$createTextNode, $isParagraphNode, $isTextNode, LexicalNode, TextNode} from 'lexical'
import {$createImageNode, $isImageNode, ImageNode} from "../../plugins/image/node/node"
import emojiList from "../../plugins/emoji/emoji-list"
import {createTableTransformer} from "./table-transformer"
import {$createLinkNode, $isLinkNode, LinkNode} from "@lexical/link"
import {$createMentionNode, $isMentionNode, MentionNode} from "../../plugins/mentions/node/node"
import {EntityType} from "../../../../gd/const/item"
import {MentionOption} from "../../plugins/mentions/option"

// const debug = "MARKDOWN_TRANSFORMERS"
const debug = false

export const HR: ElementTransformer = {
    dependencies: [HorizontalRuleNode],
    export: (node: LexicalNode) => {
        return $isHorizontalRuleNode(node) ? '***' : null;
    },
    regExp: /^(---|\*\*\*|___)\s?$/,
    replace: (parentNode, _1, _2, isImport) => {
        const line = $createHorizontalRuleNode();

        // TODO: Get rid of isImport flag
        if (isImport || parentNode.getNextSibling() != null) {
            parentNode.replace(line);
        } else {
            parentNode.insertBefore(line);
        }

        line.selectNext();
    },
    type: 'element',
};

export const IMAGE: TextMatchTransformer = {
    dependencies: [ImageNode],
    export: (node: any) => {
        if (!$isImageNode(node)) {
            return null;
        }

        return `![${node.getAltText()}](${node.getSrc()})`;
    },
    importRegExp: /!(?:\[([^[]*)\])(?:\(([^(]+)\))/,
    regExp: /!(?:\[([^[]*)\])(?:\(([^(]+)\))$/,
    replace: (textNode, match) => {
        const [, altText, src] = match;
        const imageNode = $createImageNode({
            altText,
            maxWidth: 800,
            src,
        });
        textNode.replace(imageNode);
    },
    trigger: ')',
    type: 'text-match',
};

export const EMOJI: TextMatchTransformer = {
    dependencies: [],
    export: () => null,
    importRegExp: /:([a-z0-9_]+):/,
    regExp: /:([a-z0-9_]+):$/,
    replace: (textNode, [, name]) => {
        const emoji = emojiList.find((e: any) => e.aliases.includes(name))?.emoji;
        if (emoji) {
            textNode.replace($createTextNode(emoji));
        }
    },
    trigger: ':',
    type: 'text-match',
};

/*export const EQUATION: TextMatchTransformer = {
  dependencies: [EquationNode],
  export: (node: any) => {
    if (!$isEquationNode(node)) {
      return null;
    }

    return `$${node.getEquation()}$`;
  },
  importRegExp: /\$([^$]+?)\$/,
  regExp: /\$([^$]+?)\$$/,
  replace: (textNode, match) => {
    const [, equation] = match;
    const equationNode = $createEquationNode(equation, true);
    textNode.replace(equationNode);
  },
  trigger: '$',
  type: 'text-match',
};*/

/*export const TWEET: ElementTransformer = {
  dependencies: [TweetNode],
  export: (node: any) => {
    if (!$isTweetNode(node)) {
      return null;
    }

    return `<tweet id="${node.getId()}" />`;
  },
  regExp: /<tweet id="([^"]+?)"\s?\/>\s?$/,
  replace: (textNode, _1, match) => {
    const [, id] = match;
    const tweetNode = $createTweetNode(id);
    textNode.replace(tweetNode);
  },
  type: 'element',
};*/

// Markdown table support lives in ./table-transformer - see MARKDOWN_TRANSFORMERS below.
export const LINK: ElementTransformer = {
    dependencies: [LinkNode],
    export: (node: any) => {
        if (!$isLinkNode(node)) {
            return null
        }

        return `[${node.getTitle()}](${node.getURL()})`
    },
    // regExp: /\[([^\]]*?(?:\!\[[^\]]*\]\([^\)]*\)[^\]]*)*?)\]\(([^)]+)\)$/,
    regExp: /(?:\[([^[]+)\])(?:\((?:([^()\s]+)(?:\s"((?:[^"]*\\")*[^"]*)"\s*)?)\))$/,
    replace: (parentNode, _1, match) => {
        const [, linkText, linkUrl, linkTitle] = match;
        const linkNode = $createLinkNode(linkUrl, {title: linkTitle});
        const linkTextNode = $createTextNode(linkText);
        linkTextNode.setFormat(parentNode.getFormat());
        linkNode.append(linkTextNode);
        parentNode.replace(linkNode);
    },
    type: 'element', // is processed prior to 'text-match'
}

/*export const LINK: TextMatchTransformer = {
    dependencies: [LinkNode],
    export: (node: any) => {
        if (!$isLinkNode(node)) {
            return null;
        }

        return `[${node.getTitle()}](${node.getURL()})`;
    },
    importRegExp: /\[([^\]]*?(?:\!\[[^\]]*\]\([^\)]*\)[^\]]*)*?)\]\(([^)]+)\)/,
    regExp: /\[([^\]]*?(?:\!\[[^\]]*\]\([^\)]*\)[^\]]*)*?)\]\(([^)]+)\)$/,
    replace: (textNode, match) => {
        console.log('!!!LINK', textNode, match)

        const [, altText, src] = match;
        const linkNode = $createLinkNode(src, {
            target: '_blank',
            title: altText
        });

        console.log('!!!LINK linkNode', linkNode)

        textNode.replace(linkNode);
    },
    trigger: ')',
    type: 'text-match',
}*/

// Order of text transformers matters:
//
// - code should go first as it prevents any transformations inside
// - then longer tags match (e.g. ** or __ should go before * or _)
/*export const LINK: TextMatchTransformer = {
    dependencies: [LinkNode],
    export: (node, exportChildren, exportFormat) => {
        if (!$isLinkNode(node)) {
            return null;
        }
        const title = node.getTitle();
        const linkContent = title
            ? `[${node.getTextContent()}](${node.getURL()} "${title}")`
            : `[${node.getTextContent()}](${node.getURL()})`;
        const firstChild = node.getFirstChild();
        // Add text styles only if link has single text node inside. If it's more
        // then one we ignore it as markdown does not support nested styles for links
        if (node.getChildrenSize() === 1 && $isTextNode(firstChild)) {
            return exportFormat(firstChild, linkContent);
        } else {
            return linkContent;
        }
    },
    importRegExp:
        /(?:\[([^[]+)\])(?:\((?:([^()\s]+)(?:\s"((?:[^"]*\\")*[^"]*)"\s*)?)\))/,
    regExp:
        /(?:\[([^[]+)\])(?:\((?:([^()\s]+)(?:\s"((?:[^"]*\\")*[^"]*)"\s*)?)\))$/,
    replace: (textNode, match) => {
        const [, linkText, linkUrl, linkTitle] = match;
        const linkNode = $createLinkNode(linkUrl, {title: linkTitle});
        const linkTextNode = $createTextNode(linkText);
        linkTextNode.setFormat(textNode.getFormat());
        linkNode.append(linkTextNode);
        textNode.replace(linkNode);
    },
    trigger: ')',
    type: 'text-match',
};*/

export const MENTION: TextMatchTransformer = {
    dependencies: [MentionNode],
    export: (node: any) => {
        if (!$isMentionNode(node)) {
            return null
        }

        const mentionNode = node as MentionNode;
        let prefix: string | undefined
        switch (mentionNode.__mentionData.type) {
            case EntityType.TASK:
                prefix = '@t-'
                break

            case EntityType.DOCUMENT:
                prefix = '@d-'
                break

            case EntityType.USER:
                prefix = '@u-'
                break

            case EntityType.PROJECT:
                prefix = '@p-'
                break
        }

        return '{0}{1}'.f(prefix, mentionNode.__mentionData.id)
    },
    // Regex breakdown:
    // @([tdup])       --> group 1: entity type (t/u/p/d)
    // -([A-Za-z0-9]+) --> group 2: ID (alphanumeric)
    // (\[([^\]|]+)(?:\|([^\]|]+))?\])? --> group 3: [name|extra_or_undefined]
    //   group 4: name (always present if brackets are present)
    //   group 5: extra (optional, taskTypeId if @t)
    importRegExp: /@([tdup])-([A-Za-z0-9]+)(?:\[([^\]|]+)(?:\|([^\]|]+))?\])?/,
    regExp: /@([tdup])-([A-Za-z0-9]+)(?:\[([^\]|]+)(?:\|([^\]|]+))?\])?$/,
    trigger: ')',
    replace: (textNode: TextNode, match: RegExpMatchArray) => {
        const id = match[2]
        const label = match[3]

        let mentionData: MentionOption | undefined;
        switch (match[1]) {
            case 't':
                mentionData = new MentionOption({
                    id      : id,
                    name    : label || 'Task name',
                    type    : EntityType.TASK,
                    data    : {
                        id          : id,
                        title       : label || 'Task name',
                        taskTypeId  : match[4]
                    },
                })
                break

            case 'd':
                mentionData = new MentionOption({
                    id      : id,
                    name    : label || 'Document name',
                    type    : EntityType.DOCUMENT,
                    data    : {
                        id      : id,
                    },
                })
                break

            case 'u':
                const user = gd.session.users.get(id)
                mentionData = new MentionOption({
                    id      : id,
                    name    : label || user?.name || 'User name',
                    type    : EntityType.USER,
                    data    : user || {
                        id      : id,
                    },
                })
                break

            case 'p':
                const project = gd.session.projects.get(id)
                mentionData = new MentionOption({
                    id      : id,
                    name    : label || project?.name || 'Project name',
                    type    : EntityType.PROJECT,
                    data    : project || {
                        id      : id,
                    },
                })
                break
        }

        // replaces only the mention text, preserving rest for other transformers (e.g. lists)
        const mentionNode = $createMentionNode(mentionData)
        textNode.replace(mentionNode)
    },
    type: 'text-match',
};

// Everything except TABLE - used to convert individual table cells, so including TABLE would let
// the exporter recurse back into itself
const CELL_TRANSFORMERS: Array<Transformer> = [
    LINK,
    HR,
    IMAGE,
    EMOJI,
    CHECK_LIST,
    ...ELEMENT_TRANSFORMERS,
    ...TEXT_FORMAT_TRANSFORMERS,
    ...TEXT_MATCH_TRANSFORMERS,
    MENTION,
]

export const TABLE = createTableTransformer(() => {
    return CELL_TRANSFORMERS
})

export const MARKDOWN_TRANSFORMERS: Array<Transformer> = [
    LINK,
    TABLE,
    HR,
    IMAGE,
    EMOJI,
    // EQUATION,
    // TWEET,
    CHECK_LIST,
    ...ELEMENT_TRANSFORMERS,
    // ...MULTILINE_ELEMENT_TRANSFORMERS,
    ...TEXT_FORMAT_TRANSFORMERS,
    ...TEXT_MATCH_TRANSFORMERS,
    MENTION,
]
